import { useMemo } from "react";
import { Play, Pause, SkipBack, SkipForward } from "@phosphor-icons/react";

// Jun 2026 (Randy) — ultra-thin transport for the whole-conversation read,
// sits right under the three listen buttons (public Q&A + AI Assistant).
//
// Sep 2026 (Randy) — pulled back to the ORIGINAL plan after the interval-dot
// version buried the line in dots. Stops are bubble boundaries and nothing
// else: one dot where a question starts, one (slightly larger) where its answer
// starts. Tap the answer dot to skip a long question. No dots inside an answer,
// no drag-seek.
//
// The drawer can also be SCOPED to a single exchange: the parent passes just
// that pair's two stops while a per-bubble play is running. Purely
// presentational — the parent owns playback, scope and the labels.

const MAX_POS_PCT = 96; // dots span this much of the line; 2% inset each side
// pairs: [{ qMsgIdx, qText, aMsgIdx, aText }] → flat stop list.
export function buildScrubStops(pairs) {
  const out = [];
  (pairs || []).forEach((p, si) => {
    const qText = (p.qText || "").trim();
    if (qText && p.qMsgIdx !== p.aMsgIdx) {
      out.push({ kind: "q", segIdx: si, msgIdx: p.qMsgIdx, label: `Q${si + 1} · ${qText.slice(0, 54)}` });
    }
    if ((p.aText || "").trim()) {
      out.push({ kind: "a", segIdx: si, msgIdx: p.aMsgIdx, label: `Answer ${si + 1} · skip the question` });
    }
  });
  return out;
}

// Which stop is sounding right now — the playback tick reports a message index,
// and a question and its answer are different messages, so that's all we need.
export function stopIndexFor(stops, msgIdx) {
  if (!stops?.length || typeof msgIdx !== "number" || msgIdx < 0) return -1;
  return stops.findIndex((s) => s.msgIdx === msgIdx);
}

export const ConversationScrubber = ({
  stops = [],
  currentStop = -1,
  frac = 0,
  playing = false,
  onPlayPause,
  onPrev,
  onNext,
  onJump,
  color = "#4A5D4E",
  testidPrefix = "scrubber",
}) => {
  const total = stops.length;

  // Evenly spaced — every stop is one bubble, so there's nothing to weight.
  // Each stop owns an equal slice of the line (so with one exchange the question
  // is the first half and the answer the second), inset 2% so the first dot
  // isn't clipped by the edge.
  const positions = useMemo(() => {
    if (!total) return [];
    const step = MAX_POS_PCT / total;
    return stops.map((_, i) => 2 + i * step);
  }, [stops, total]);

  const fillPct = useMemo(() => {
    if (currentStop < 0 || !positions.length) return 0;
    const from = positions[Math.min(currentStop, positions.length - 1)] ?? 0;
    const to = currentStop + 1 < positions.length ? positions[currentStop + 1] : 98;
    const f = Math.min(1, Math.max(0, frac || 0));
    return from + (to - from) * f;
  }, [currentStop, positions, frac]);

  if (total < 1) return null;

  const ctrlCls =
    "shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-black/5 active:bg-black/10 transition-colors";

  return (
    <div className="w-full flex items-center gap-2 px-[3%] sm:px-[10%] mt-1.5" data-testid={`${testidPrefix}-bar`}>
      <button type="button" onClick={onPrev} aria-label="Previous part" data-testid={`${testidPrefix}-prev`} className={ctrlCls} style={{ color }}>
        <SkipBack size={13} weight="fill" />
      </button>
      <button type="button" onClick={onPlayPause} aria-label={playing ? "Pause" : "Play"} data-testid={`${testidPrefix}-playpause`} className={ctrlCls} style={{ color }}>
        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
      </button>

      <div className="relative flex-1 h-5 flex items-center" data-testid={`${testidPrefix}-line`}>
        <div className="absolute left-0 right-0 h-[3px] rounded-full" style={{ background: "rgba(0,0,0,0.12)" }} />
        <div
          className="absolute left-0 h-[3px] rounded-full transition-all duration-500 ease-linear"
          style={{ width: `${fillPct}%`, background: color }}
          data-testid={`${testidPrefix}-fill`}
        />
        {stops.map((s, i) => {
          const reached = i <= currentStop;
          const size = s.kind === "a" ? 13 : 9;
          const bw = s.kind === "a" ? 2.5 : 1.5;
          return (
            <button
              key={`${s.msgIdx}-${s.kind}`}
              type="button"
              onClick={() => onJump?.(i)}
              data-testid={`${testidPrefix}-dot-${i}`}
              data-stop-kind={s.kind}
              title={s.label}
              aria-label={s.label}
              className="absolute -translate-x-1/2 flex items-center justify-center group"
              style={{ left: `${positions[i]}%`, width: 22, height: 22 }}
            >
              <span
                className="rounded-full transition-transform duration-200 group-hover:scale-150"
                style={{ width: size, height: size, background: reached ? color : "#fff", border: `${bw}px solid ${color}` }}
              />
            </button>
          );
        })}
      </div>

      <button type="button" onClick={onNext} aria-label="Next part" data-testid={`${testidPrefix}-next`} className={ctrlCls} style={{ color }}>
        <SkipForward size={13} weight="fill" />
      </button>
    </div>
  );
};

export default ConversationScrubber;
